import { GenericHighlight } from "@/types/Highlights";

export interface ModifiedItem {
  oldValue: string;
  newValue: string;
  id: string;
}

export interface DiffResult {
  added: GenericHighlight[];
  removed: GenericHighlight[];
  modified: ModifiedItem[];
  merged: GenericHighlight[];
}

/**
 * Get items that were added (exist in new but not in old)
 */
export function getAddedItems(
  oldArray: GenericHighlight[] | undefined,
  newArray: GenericHighlight[] | undefined,
  key: keyof GenericHighlight
) {
  if (!newArray) {
    return [];
  }
  return newArray.filter((newItem) => !oldArray?.some((oldItem) => oldItem[key] === newItem[key]));
}

/**
 * Get items that were removed (exist in old but not in new)
 */
export function getRemovedItems(
  oldArray: GenericHighlight[] | undefined,
  newArray: GenericHighlight[] | undefined,
  key: keyof GenericHighlight
) {
  if (!newArray) {
    return [];
  }
  return oldArray?.filter((oldItem) => !newArray?.some((newItem) => newItem[key] === oldItem[key])) || [];
}

/**
 * Get items that were modified (same id/code but different content)
 * Compares by id (default) or specified key to find items that exist in both arrays but with different names
 */
export function getModifiedItems(
  oldArray: GenericHighlight[] | undefined,
  newArray: GenericHighlight[] | undefined,
  key: keyof GenericHighlight = "id"
): ModifiedItem[] {
  if (!oldArray || !newArray) {
    return [];
  }

  const modified: ModifiedItem[] = [];

  // For each new item, check if it exists in old array with different name
  for (const newItem of newArray) {
    const oldItem = oldArray.find((old) => {
      // Match by specified key if both items have it
      const newValue = newItem[key];
      const oldValue = old[key];
      if (newValue && oldValue) {
        return oldValue === newValue;
      }
      // If key not available, can't reliably detect modifications
      return false;
    });

    // If found and names are different, it's a modification
    if (oldItem && oldItem.name !== newItem.name) {
      modified.push({
        oldValue: oldItem.name,
        newValue: newItem.name,
        id: (newItem[key] as string) || newItem.name,
      });
    }
  }

  return modified;
}

/**
 * Merge two arrays and remove duplicates
 *
 * @param oldArray - The old array
 * @param newArray - The new array
 * @param key - The key to use to compare the items
 */
export function mergeUnique(
  oldArray: GenericHighlight[] | undefined,
  newArray: GenericHighlight[] | undefined,
  key: keyof GenericHighlight
) {
  const mergedArray = (oldArray || []).concat(newArray || []);
  // Remove duplicates
  return mergedArray.filter((item, index, self) => self.findIndex((v) => v[key] === item[key]) === index);
}

/**
 * Get all diff information for highlights in a single call
 * Returns added, removed, modified items and a merged array for display
 * The merged array preserves visualization_order, prioritizing new items
 *
 * @param oldArray - The old array of highlights
 * @param newArray - The new array of highlights
 * @param key - The key to use for comparison (default: "id")
 */
export function getDiffItems(
  oldArray: GenericHighlight[] | undefined,
  newArray: GenericHighlight[] | undefined,
  key: keyof GenericHighlight = "id"
): DiffResult {
  const oldCustom = oldArray || [];
  const newCustom = newArray || [];

  // Use generic functions with the specified key
  const added = getAddedItems(oldCustom, newCustom, key);
  const removed = getRemovedItems(oldCustom, newCustom, key);
  const modified = getModifiedItems(oldCustom, newCustom, key);

  // Merge arrays preserving visualization_order
  // Priority: new items in their positions, then removed items in their original positions
  const allItems = [...newCustom, ...removed];
  const merged = allItems.toSorted((a, b) => (a.visualization_order || 0) - (b.visualization_order || 0));

  return { added, removed, modified, merged };
}
